import React, { useRef, useState } from 'react';
import { Box, TextField, Tooltip, Typography } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

/**
 * A spreadsheet cell for 내용 / 담당자 연결 / 처리현황: bordered like Excel, double-click to type, click outside
 * or Enter to finish (Shift+Enter for a new line, Esc to cancel). Untouched text is never sent.
 *
 * One writer at a time (docs/MISSED_CALLS_PLAN.md §5.12): opening the cell claims it for this admin and every
 * other page greys it out — *민지 입력 중…* — until it is saved, cancelled, or the writer's browser goes away.
 * The claim is a courtesy, not a guarantee: `onSave` still reports the server's 409 when two people slip
 * through at once, and the page then offers 그래도 저장.
 *
 * The editor stays open until the save lands. A save that fails — or a 그래도 저장 the admin declines — keeps what was
 * typed (Enter tries again, Esc gives it up); a saved cell tells the page (`onSaved`), so it stops refreshing a claim
 * the server already let go. `onSave` resolves to something truthy once it is saved.
 */
const EditableTextCell = ({
    value, onSave, disabled = false, placeholder = '—', maxLength = 5000, minWidth = 160,
    lockedBy = null, onClaim, onRelease, onSaved,
}) => {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState('');
    const [saving, setSaving] = useState(false);
    const [failed, setFailed] = useState(false);   // the last save didn't land: the draft stays open
    const busy = useRef(false);
    const input = useRef(null);
    const startValue = useRef('');
    const cancelled = useRef(false);
    const current = value ?? '';
    const changedElsewhere = editing && current !== startValue.current;

    const start = async () => {
        if (disabled || saving || lockedBy) return;
        if (onClaim) {
            const out = await onClaim();
            if (out && out.ok === false) return;   // someone got there first; the broadcast greys it out
        }
        startValue.current = current;
        cancelled.current = false;
        setFailed(false);
        setDraft(current);
        setEditing(true);
    };

    const finish = async () => {
        if (cancelled.current || busy.current) return;
        if (draft === startValue.current) {
            setEditing(false);
            if (onRelease) onRelease();   // untouched: hand the cell straight back
            return;
        }
        busy.current = true;
        setSaving(true);
        let saved = null;
        try {
            saved = await onSave(draft);  // the page reports what went wrong
        } catch {
            saved = null;
        } finally {
            busy.current = false;
            setSaving(false);
        }
        if (saved) {
            setEditing(false);
            setFailed(false);
            if (onSaved) onSaved();       // the save let the cell go on the server; stop refreshing the claim
        } else {
            setFailed(true);              // keep the draft, and the focus for Enter
            if (input.current) input.current.focus();
        }
    };

    if (editing) {
        return (
            <Box sx={{ minWidth }}>
                <TextField
                    value={draft}
                    onChange={(e) => setDraft(e.target.value.slice(0, maxLength))}
                    inputRef={input}
                    onBlur={failed ? undefined : finish}   // after a failed save, clicking away doesn't send it again
                    onKeyDown={(e) => {
                        if (e.key === 'Escape') {
                            e.preventDefault();
                            if (busy.current) return;
                            cancelled.current = true;
                            setFailed(false);
                            setEditing(false);
                            if (onRelease) onRelease();
                        } else if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();   // Enter finishes the cell, Shift+Enter starts a line
                            if (failed) finish(); else e.target.blur();
                        }
                    }}
                    multiline
                    minRows={2}
                    fullWidth
                    size="small"
                    autoFocus
                    inputProps={{ style: { fontSize: 13 }, readOnly: saving }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 0.5, p: 0.75 } }}
                />
                {changedElsewhere && (
                    <Typography variant="caption" color="warning.main" display="block">
                        다른 관리자가 수정함 — 저장하면 덮어씁니다
                    </Typography>
                )}
                {saving && <Typography variant="caption" color="text.secondary" display="block">저장 중…</Typography>}
                {failed && !saving && (
                    <Typography variant="caption" color="error" display="block">
                        저장하지 못했습니다 — Enter로 다시 저장, Esc로 취소
                    </Typography>
                )}
            </Box>
        );
    }

    const locked = Boolean(lockedBy);
    const cell = (
        <Typography
            variant="body2"
            onDoubleClick={start}
            sx={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                userSelect: 'none',                 // …so a double-click opens the cell instead of selecting a word
                cursor: disabled || locked ? 'default' : 'cell',
                minHeight: 34,
                minWidth,
                px: 0.75,
                py: 0.5,
                border: '1px solid',
                borderColor: locked ? 'warning.light' : 'divider',
                borderRadius: 0.5,
                color: 'inherit',
                opacity: saving ? 0.6 : 1,
                backgroundColor: locked ? 'action.disabledBackground' : undefined,
                '&:hover': disabled || locked ? {} : { borderColor: 'text.disabled', backgroundColor: 'action.hover' },
            }}
        >
            {current || <Box component="span" sx={{ opacity: 0.35 }}>{placeholder}</Box>}
            {locked && (
                <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.25, mt: 0.25, opacity: 0.7 }}>
                    <LockOutlinedIcon sx={{ fontSize: 12 }} />
                    <Typography variant="caption" component="span">{lockedBy.name || '다른 관리자'} 입력 중…</Typography>
                </Box>
            )}
        </Typography>
    );

    if (disabled) return cell;
    return <Tooltip title={locked ? `${lockedBy.name || '다른 관리자'}가 입력 중입니다` : '더블클릭해서 입력'}>{cell}</Tooltip>;
};

export default EditableTextCell;
